import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { TPL_FORM } from "@/constants/testIds";
import { MONTHS } from "@/components/DateFilter";

const THIS_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 6 }, (_, i) => THIS_YEAR - 1 + i);

const EMPTY = { title: "", content: "", category_id: "none", keywords: "", months: [], year: "none" };

export const TemplateDialog = ({ open, onOpenChange, template, categories, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(template ? { title: template.title, content: template.content, category_id: template.category_id || "none", keywords: (template.keywords || []).join(", "), months: template.months || [], year: template.year ? String(template.year) : "none" } : EMPTY);
    }
  }, [open, template]);

  const toggleMonth = (n) => setForm((f) => ({ ...f, months: f.months.includes(n) ? f.months.filter((x) => x !== n) : [...f.months, n].sort((a, b) => a - b) }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = { title: form.title, content: form.content, category_id: form.category_id === "none" ? null : form.category_id, keywords: form.keywords.split(","), months: form.months, year: form.year === "none" ? null : Number(form.year) };
    try {
      if (template) await api.put(`/templates/${template.id}`, body);
      else await api.post("/templates", body);
      toast.success(template ? "Template diperbarui" : "Template ditambahkan");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl" data-testid={TPL_FORM.dialog}>
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">{template ? "Edit Template" : "Tambah Template"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label>Judul</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required placeholder="Contoh: Info jadwal keberangkatan" data-testid={TPL_FORM.title} />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Kategori</Label>
              <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
                <SelectTrigger data-testid={TPL_FORM.category}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Tanpa kategori</SelectItem>
                  {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Kata kunci</Label>
              <Input value={form.keywords} onChange={(e) => setForm({ ...form, keywords: e.target.value })} placeholder="pisahkan dengan koma" data-testid={TPL_FORM.keywords} />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Periode (opsional)</Label>
            <div className="flex flex-wrap gap-1.5">
              {MONTHS.map((m, i) => {
                const on = form.months.includes(i + 1);
                return (
                  <button key={m} type="button" onClick={() => toggleMonth(i + 1)} data-testid={`${TPL_FORM.month}-${i + 1}`}
                    className={`rounded-full border px-3 py-1 text-xs transition-colors ${on ? "border-brand bg-brand text-white" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                    {m.slice(0, 3)}
                  </button>
                );
              })}
            </div>
            <Select value={form.year} onValueChange={(v) => setForm({ ...form, year: v })}>
              <SelectTrigger className="w-40" data-testid={TPL_FORM.year}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Tanpa tahun</SelectItem>
                {[...new Set([...YEARS, ...(template?.year ? [template.year] : [])])].sort().map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Isi jawaban</Label>
            <Textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} required rows={9} placeholder="Tulis jawaban yang akan disalin oleh CS..." data-testid={TPL_FORM.content} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-full" onClick={() => onOpenChange(false)}>Batal</Button>
            <Button type="submit" disabled={saving} className="rounded-full bg-brand hover:bg-brand-dark text-white" data-testid={TPL_FORM.submit}>
              {saving ? "Menyimpan..." : "Simpan"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
